/**
 * Full-width banner ad component
 * Lazy loaded with intersection observer for performance
 */

import { memo, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Sparkles } from "lucide-react";
import { Ad, trackAdImpression, trackAdClick } from "@/hooks/useAds";
import { Link } from "react-router-dom";

interface AdBannerProps {
  ad: Ad;
  className?: string;
}

const AdBanner = memo(({ ad, className = "" }: AdBannerProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Lazy load with intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          trackAdImpression(ad.id);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "100px" }
    );

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => observer.disconnect();
  }, [ad.id]);

  const handleClick = () => {
    trackAdClick(ad.id);
  };

  const isExternal = ad.link_url?.startsWith('http');

  const content = (
    <>
      {/* Skeleton loader */}
      {!imageLoaded && ad.image_url && ad.image_url !== '/placeholder.svg' && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}

      {/* Image */}
      {isVisible && ad.image_url && ad.image_url !== '/placeholder.svg' && (
        <div className="w-full h-32 xs:h-36 sm:h-40 md:h-48 lg:h-56 overflow-hidden">
          <img
            src={ad.image_url}
            alt={ad.title}
            loading="lazy"
            decoding="async"
            onLoad={() => setImageLoaded(true)}
            className={`w-full h-full object-cover object-center transition-opacity duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </div>
      )}

      {/* Content */}
      <div className="p-3 xs:p-4 sm:p-5">
        {/* Ad badge */}
        <div className="inline-flex items-center gap-1 bg-primary/10 text-primary text-[9px] xs:text-[10px] font-medium px-1.5 xs:px-2 py-0.5 rounded-full mb-1.5 xs:mb-2">
          <Sparkles className="w-2.5 h-2.5 xs:w-3 xs:h-3" />
          <span>Sponsored</span>
        </div>
        
        <h3 className="text-sm xs:text-base sm:text-lg md:text-xl font-bold text-foreground mb-0.5 xs:mb-1 line-clamp-1 group-hover:text-primary transition-colors">
          {ad.title}
        </h3>
        
        {ad.description && (
          <p className="text-muted-foreground text-[10px] xs:text-xs sm:text-sm line-clamp-2 mb-1.5 xs:mb-2 sm:mb-3">
            {ad.description}
          </p>
        )}

        <motion.div 
          className="inline-flex items-center gap-1 xs:gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-2 py-1 xs:px-3 xs:py-1.5 sm:px-4 sm:py-2 rounded-full text-[10px] xs:text-xs sm:text-sm font-medium transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Learn More
          <ExternalLink className="w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-4 sm:h-4" />
        </motion.div>
      </div>
    </>
  );

  return (
    <motion.div
      ref={bannerRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
      transition={{ duration: 0.5 }}
      className={`relative overflow-hidden rounded-xl bg-card border border-border/50 shadow-sm ${className}`}
    >
      {isExternal ? (
        <a
          href={ad.link_url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="block relative group cursor-pointer"
        >
          {content}
        </a>
      ) : (
        <Link
          to={ad.link_url || '/'}
          onClick={handleClick}
          className="block relative group cursor-pointer"
        >
          {content}
        </Link>
      )}
    </motion.div>
  );
});

AdBanner.displayName = 'AdBanner';

export default AdBanner;
